<template>
  <div class="flex items-center justify-center">
    <button class="absolute" @click="toggleMenu">
      <HamburgerIcon class="h-6 w-9 sm:h-10 sm:w-14" />
    </button>

    <Transition
      enter-active-class="duration-500 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="duration-500 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="isMenuOpen"
        class="fixed top-0 z-50 h-screen w-screen bg-avu-gray px-12 py-14"
      >
        <button class="ml-auto flex" @click="closeMenu">
          <XMarkIcon class="h-8 w-8" />
        </button>

        <div class="flex h-full flex-col items-start gap-[15vw]">
          <ClientOnly>
            <Transition
              mode="out-in"
              enter-active-class="duration-300 ease-out"
              enter-from-class="-translate-x-full opacity-0"
              enter-to-class="translate-x-0 opacity-100"
            >
              <div
                v-if="currentButtonIndex === null && !isSubMenuVisible"
                :key="`menu-${currentButtonIndex}-${isSubMenuVisible}`"
                class="mt-[8vw] flex flex-col gap-7 md:mt-[5vw]"
              >
                <NavButton
                  v-for="(button, index) in headerButtons"
                  :key="button.key"
                  :text="button.text"
                  :link="!button.links ? button.link : null"
                  :class="['uppercase']"
                  @click="button.links ? openSubMenu(index) : closeMenu()"
                />
              </div>
            </Transition>
          </ClientOnly>

          <Transition
            enter-active-class="duration-500 ease-out"
            enter-from-class="translate-x-full opacity-0"
            enter-to-class="translate-x-0 opacity-100"
            leave-active-class="duration-500 ease-in"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="translate-x-full opacity-0"
            @before-leave="isSubMenuVisible = true"
            @after-leave="resetSubMenu"
          >
            <div
              v-if="currentButtonIndex !== null"
              class="mt-7 flex flex-col gap-[47px]"
            >
              <button
                class="flex items-center gap-5 uppercase text-avu-black"
                @click="closeSubMenu"
              >
                ← Back
              </button>
              <div class="flex flex-col gap-7">
                <NavButton
                  v-for="(button, index) in headerButtons[currentButtonIndex]
                    .links"
                  :key="index"
                  :text="button.text"
                  :link="button.link"
                  @click="closeMenu"
                />
              </div>
            </div>
          </Transition>

          <div
            class="flex items-center justify-center gap-5 text-avu-black md:mb-[77px]"
          >
            <a
              v-for="social in socialData"
              :key="social.type"
              :href="social.url"
              target="_blank"
            >
              <component :is="getSocialIcon(social.type)" class="h-8 w-8" />
            </a>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import InstagramIcon from "~/components/icons/InstagramIcon.vue";
  import FacebookIcon from "~/components/icons/FacebookIcon.vue";
  import YouTubeIcon from "~/components/icons/YouTubeIcon.vue";

  const isMenuOpen = ref(false);
  const currentButtonIndex = ref(null);
  const isSubMenuVisible = ref(false);
  const socialData = ref([]);

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;

    document.body.style.overflow = "hidden";
  };

  const closeMenu = () => {
    isMenuOpen.value = false;
    currentButtonIndex.value = null;
    isSubMenuVisible.value = false;

    document.body.style.overflow = "auto";
  };

  const openSubMenu = (index) => {
    currentButtonIndex.value = index;
  };

  const closeSubMenu = () => {
    isSubMenuVisible.value = true;
    setTimeout(() => {
      currentButtonIndex.value = null;
      isSubMenuVisible.value = false;
    }, 300);
  };

  const resetSubMenu = () => {
    isSubMenuVisible.value = false;
  };

  const headerButtons = [
    {
      text: "L<span class='text-avu-blue'>a</span><span class='text-avu-pink'>r</span>ge",
      link: "/",
    },
    {
      text: "ABOUT",
      key: "about",
      link: "/about",
      links: [
        { text: "About LARGE", link: "/about/about-large" },
        { text: "Artistic Research", link: "/about/artistic-research" },
        { text: "People", link: "/about/people" },
        { text: "Podcast", link: "/about/podcast" },
        { text: "Contact", link: "/about/contact" },
      ],
    },
    {
      text: "Events",
      key: "events",
      link: "/events",
      links: [],
    },
    {
      text: "Publications",
      key: "publications",
      link: "/publications",
      links: [],
    },
    { text: "Documents", key: "documents", link: "/documents" },
    { text: "Courses", key: "courses", link: "/courses" },
    { text: "Research", key: "research", link: "/research" },
    { text: "News", key: "news", link: "/news" },
  ];

  const { data: queryData } = await usePersistedFetch("header");

  const fetchSocialData = async () => {
    const { data: footerData } = await usePersistedFetch("footer", {}, { server: false });
    socialData.value =
      footerData.value?.settings?.footer?.social_networks || [];
  };

  onMounted(async () => {
    window.addEventListener("resize", () => {
      if (window.innerWidth < 1024) {
        fetchSocialData();
      }
    });
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", fetchSocialData);
  });

  const getSocialIcon = (type) => {
    switch (type) {
      case "instagram":
        return InstagramIcon;
      case "facebook":
        return FacebookIcon;
      case "youtube":
        return YouTubeIcon;
      default:
        return null;
    }
  };

  watchEffect(() => {
    const eventButton = headerButtons.find((button) => button.key === "events");
    const publicationButton = headerButtons.find(
      (button) => button.key === "publications"
    );

    if (eventButton) {
      eventButton.links = [];
      queryData.value?.eventCategories.forEach((category) => {
        eventButton.links.push({
          text: category.name,
          link: `/events/${category.slug}`,
        });
      });
    }

    if (publicationButton) {
      publicationButton.links = [];
      queryData.value?.publications
        .filter((publication) => !publication.parent)
        .forEach((publication) => {
          publicationButton.links.push({
            text: publication.name,
            link: `/publication/${publication.slug}`,
          });
        });
    }
  });
</script>